import {getQepSystem} from '../utils/get-qep-system';

export const COLUMN_PREFERENCES_KEY = 'columnPreferences';
export const COLUMN_PREFERENCES_VERSION = 1;

export type ColumnPreferenceColumn = {
  id: string;
  label: string;
  visible: boolean;
  locked?: boolean;
};

export type ColumnPreferenceSet = {
  sourceAppId?: string;
  columns: ColumnPreferenceColumn[];
};

export type ColumnPreferences = {
  version: number;
  global?: ColumnPreferenceSet;
  apps?: Record<string, ColumnPreferenceSet>;
};

type ColumnPreferenceSource = {
  colId?: string;
  field?: string;
  headerName?: string;
  initialHide?: boolean;
  hide?: boolean;
  hidden?: boolean;
  lockVisible?: boolean;
};

type MatchableColumn = Pick<ColumnPreferenceColumn, 'id' | 'label'>;

type ColumnStateWithId = {
  colId: string;
};

const MATCH_GROUPS: Record<string, string[]> = {
  account: ['account', 'strategy', 'executiontag', 'defaultstrategy'],
  ask: ['ask'],
  askSize: ['asksize'],
  averageFillPrice: ['averagefillpx', 'averagefillprice', 'avgfillpx', 'avgfillprice'],
  bid: ['bid'],
  bidSize: ['bidsize'],
  broker: ['broker'],
  businessUnit: ['businessunit'],
  callPut: ['callput', 'putcall'],
  currency: ['currency', 'ccy'],
  description: ['description', 'memo'],
  displayQuantity: ['displayqty', 'displayquantity'],
  exchange: ['exchange', 'exch', 'segmentmic'],
  expirationDate: ['expiry', 'expirationdate', 'expiretime', 'maturitymonthyear'],
  last: ['last', 'currentprice', 'orderpx', 'orderprice'],
  price: ['price', 'px', 'stopprice', 'strike'],
  quantity: ['qty', 'quantity', 'orderqty', 'currentposition', 'position'],
  side: ['side', 'orderside', 'direction'],
  status: ['status'],
  symbol: ['symbol', 'product', 'uniquesymbol', 'name', 'refsymbol', 'fixsymbol', 'exchangesymbol'],
  tif: ['tif', 'timeinforce'],
  trader: ['trader', 'traderid'],
  type: ['type', 'ordertype', 'securitytype', 'algotype'],
  volume: ['volume', 'totaltraded', 'lastquantity', 'totalfillqty', 'fillqty', 'filledqty', 'leavesqty'],
};

const MATCH_GROUP_LOOKUP = Object.entries(MATCH_GROUPS).reduce<Record<string, string>>((result, [group, aliases]) => {
  for (const alias of aliases) {
    result[alias] = group;
  }

  return result;
}, {});

const EMPTY_PREFERENCES: ColumnPreferences = {
  version: COLUMN_PREFERENCES_VERSION,
  apps: {},
};

function compactToken(value: string) {
  return value
    .toLowerCase()
    .replaceAll(/[^a-z\d]+/g, ' ')
    .trim()
    .replaceAll(/\s+/g, '');
}

function getMatchTokens(value: string) {
  const compactValue = compactToken(value);
  if (compactValue === '') {
    return [];
  }

  const tokens = new Set([`exact:${compactValue}`]);
  const group = MATCH_GROUP_LOOKUP[compactValue];
  if (group) {
    tokens.add(`group:${group}`);
  }

  return [...tokens];
}

function getColumnMatchTokens(column: MatchableColumn) {
  return new Set([
    ...getMatchTokens(column.id),
    ...getMatchTokens(column.label),
  ]);
}

function getMatchScore(sourceColumn: MatchableColumn, targetColumn: MatchableColumn) {
  const sourceId = compactToken(sourceColumn.id);
  const sourceLabel = compactToken(sourceColumn.label);
  const targetId = compactToken(targetColumn.id);
  const targetLabel = compactToken(targetColumn.label);

  if (sourceId !== '' && sourceId === targetId) {
    return 400;
  }

  if (sourceLabel !== '' && sourceLabel === targetLabel) {
    return 300;
  }

  if ((sourceId !== '' && sourceId === targetLabel) || (sourceLabel !== '' && sourceLabel === targetId)) {
    return 220;
  }

  const sourceTokens = getColumnMatchTokens(sourceColumn);
  const targetTokens = getColumnMatchTokens(targetColumn);
  let score = 0;

  for (const token of sourceTokens) {
    if (targetTokens.has(token)) {
      score += token.startsWith('group:') ? 40 : 80;
    }
  }

  return score;
}

function sanitizeColumnPreferenceColumn(value: unknown): ColumnPreferenceColumn | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const candidate = value as Partial<ColumnPreferenceColumn>;
  if (typeof candidate.id !== 'string' || typeof candidate.label !== 'string' || typeof candidate.visible !== 'boolean') {
    return undefined;
  }

  return {
    id: candidate.id,
    label: candidate.label,
    visible: candidate.visible,
    locked: candidate.locked === true,
  };
}

function sanitizeColumnPreferenceSet(value: unknown): ColumnPreferenceSet | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const candidate = value as Partial<ColumnPreferenceSet>;
  if (!Array.isArray(candidate.columns)) {
    return undefined;
  }

  const columns = candidate.columns
    .map(sanitizeColumnPreferenceColumn)
    .filter((column): column is ColumnPreferenceColumn => column !== undefined);

  return {
    sourceAppId: typeof candidate.sourceAppId === 'string' ? candidate.sourceAppId : undefined,
    columns,
  };
}

export function sanitizeColumnPreferences(value: unknown): ColumnPreferences {
  if (!value || typeof value !== 'object') {
    return EMPTY_PREFERENCES;
  }

  const candidate = value as Partial<ColumnPreferences>;
  const global = sanitizeColumnPreferenceSet(candidate.global);
  const apps = Object.entries(candidate.apps ?? {}).reduce<Record<string, ColumnPreferenceSet>>((result, [appId, set]) => {
    const sanitizedSet = sanitizeColumnPreferenceSet(set);
    if (sanitizedSet) {
      result[appId] = sanitizedSet;
    }

    return result;
  }, {});

  return {
    version: COLUMN_PREFERENCES_VERSION,
    ...(global ? {global} : {}),
    apps,
  };
}

export function createColumnPreferenceSet(columns: readonly ColumnPreferenceColumn[], sourceAppId?: string): ColumnPreferenceSet {
  return {
    ...(sourceAppId ? {sourceAppId} : {}),
    columns: columns.map(column => ({
      id: column.id,
      label: column.label,
      visible: column.visible,
      ...(column.locked ? {locked: true} : {}),
    })),
  };
}

export function replaceColumnPreferenceSet(
  preferences: ColumnPreferences,
  scope: 'global' | string,
  set?: ColumnPreferenceSet,
): ColumnPreferences {
  const sanitized = sanitizeColumnPreferences(preferences);
  if (scope === 'global') {
    return {
      ...sanitized,
      ...(set ? {global: set} : {}),
      ...(set ? {} : {global: undefined}),
    };
  }

  const apps = {...sanitized.apps};
  if (set) {
    apps[scope] = set;
  } else {
    delete apps[scope];
  }

  return {
    ...sanitized,
    apps,
  };
}

export function resolveColumnPreferenceSet(preferences: ColumnPreferences, appId: string) {
  const sanitized = sanitizeColumnPreferences(preferences);
  return sanitized.apps?.[appId] ?? sanitized.global;
}

export function toColumnPreferenceColumns(columns: readonly ColumnPreferenceSource[]): ColumnPreferenceColumn[] {
  return columns.reduce<ColumnPreferenceColumn[]>((result, column) => {
    const id = (column.colId ?? column.field ?? '').trim();
    const label = (column.headerName ?? id).trim();
    if (id === '' || label === '') {
      return result;
    }

    result.push({
      id,
      label,
      visible: !(column.initialHide ?? column.hide ?? column.hidden),
      locked: column.lockVisible === true,
    });

    return result;
  }, []);
}

export function applyColumnPreferenceSet(
  defaultColumns: readonly ColumnPreferenceColumn[],
  preferenceSet?: ColumnPreferenceSet,
): ColumnPreferenceColumn[] {
  if (!preferenceSet || preferenceSet.columns.length === 0) {
    return defaultColumns.map(column => ({...column}));
  }

  const matchedIds = new Set<string>();
  const orderedColumns: ColumnPreferenceColumn[] = [];

  for (const sourceColumn of preferenceSet.columns) {
    let bestMatch: ColumnPreferenceColumn | undefined;
    let bestScore = 0;

    for (const targetColumn of defaultColumns) {
      if (matchedIds.has(targetColumn.id)) {
        continue;
      }

      const score = getMatchScore(sourceColumn, targetColumn);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = targetColumn;
      }
    }

    if (!bestMatch || bestScore === 0) {
      continue;
    }

    matchedIds.add(bestMatch.id);
    orderedColumns.push({
      ...bestMatch,
      visible: bestMatch.locked ? true : sourceColumn.visible,
    });
  }

  for (const column of defaultColumns) {
    if (!matchedIds.has(column.id)) {
      orderedColumns.push({...column});
    }
  }

  return orderedColumns;
}

export function mapColumnPreferenceSetToColumns(
  defaultColumns: readonly ColumnPreferenceColumn[],
  preferenceSet: ColumnPreferenceSet,
  sourceAppId?: string,
) {
  return createColumnPreferenceSet(
    applyColumnPreferenceSet(defaultColumns, preferenceSet),
    sourceAppId ?? preferenceSet.sourceAppId,
  );
}

export function buildColumnStateFromPreferenceColumns(columns: readonly ColumnPreferenceColumn[]) {
  return columns.map(column => ({
    colId: column.id,
    hide: column.locked ? false : !column.visible,
  }));
}

/**
 * Rewrites recognized legacy preference ids before score-based matching. An exact current label
 * disambiguates historical duplicate ids, while unmatched labels retain the caller's canonical alias.
 */
function migrateColumnPreferenceSetIds(
  defaultColumns: readonly ColumnPreferenceColumn[],
  preferenceSet: ColumnPreferenceSet,
  aliases: Readonly<Record<string, string>>,
): ColumnPreferenceSet {
  const targetIdsByLabel = new Map<string, string>();
  const ambiguousLabels = new Set<string>();
  const aliasedTargetIds = new Set(Object.values(aliases));

  for (const column of defaultColumns) {
    if (!aliasedTargetIds.has(column.id)) {
      continue;
    }

    const label = compactToken(column.label);
    if (targetIdsByLabel.has(label)) {
      ambiguousLabels.add(label);
    } else {
      targetIdsByLabel.set(label, column.id);
    }
  }

  /** Maps one recognized historical id to its unambiguous current id without changing saved metadata. */
  const migratePreferenceColumn = (column: ColumnPreferenceColumn): ColumnPreferenceColumn => {
    const aliasedId = aliases[column.id];
    if (aliasedId === undefined) {
      return {...column};
    }

    const label = compactToken(column.label);
    const labelMatchedId = ambiguousLabels.has(label) ? undefined : targetIdsByLabel.get(label);
    return {...column, id: labelMatchedId ?? aliasedId};
  };

  return {
    ...preferenceSet,
    columns: preferenceSet.columns.map(migratePreferenceColumn),
  };
}

/**
 * Rewrites legacy persisted column ids while preserving state fields, order, and canonical entries.
 * When both a legacy alias and its canonical id exist, the canonical entry wins so stale state cannot
 * override a layout that has already been saved by the current application version.
 */
export function migrateColumnStateIds<TColumnState extends ColumnStateWithId>(
  columnState: readonly TColumnState[] | undefined,
  aliases: Readonly<Record<string, string>>,
): TColumnState[] | undefined {
  if (!columnState) {
    return undefined;
  }

  const canonicalIds = new Set(columnState
    .filter(column => aliases[column.colId] === undefined || aliases[column.colId] === column.colId)
    .map(column => aliases[column.colId] ?? column.colId));
  const migratedIds = new Set<string>();

  return columnState.reduce<TColumnState[]>((result, column) => {
    const migratedId = aliases[column.colId] ?? column.colId;
    const isLegacyAlias = migratedId !== column.colId;
    if (migratedIds.has(migratedId) || (isLegacyAlias && canonicalIds.has(migratedId))) {
      return result;
    }

    migratedIds.add(migratedId);
    result.push({...column, colId: migratedId});
    return result;
  }, []);
}

export async function getDefaultColumnPreferenceState(
  appId: string,
  columns: readonly ColumnPreferenceSource[],
  aliases: Readonly<Record<string, string>> = {},
) {
  try {
    const response = await getQepSystem().container.config.getPreference();
    if (!response) {
      return undefined;
    }

    const preferences = sanitizeColumnPreferences(response.data?.[COLUMN_PREFERENCES_KEY]);
    const preferenceSet = resolveColumnPreferenceSet(preferences, appId);
    if (!preferenceSet) {
      return undefined;
    }

    const defaultColumns = toColumnPreferenceColumns(columns);
    const migratedPreferenceSet = migrateColumnPreferenceSetIds(defaultColumns, preferenceSet, aliases);
    const resolvedColumns = applyColumnPreferenceSet(defaultColumns, migratedPreferenceSet);
    return buildColumnStateFromPreferenceColumns(resolvedColumns);
  } catch {
    return undefined;
  }
}
